import { useDeferredValue, useState, useEffect } from 'react';
import { Route } from '../types/route.types';
import { Filters } from '../types/filter.types';
import { filterService } from '../services/filterService';

/**
 * Fonction de filtrage par batch pour éviter de bloquer le thread principal
 */
const filterRoutesInBatches = (routesToFilter: Route[], filtersToApply: Filters): Promise<Route[]> => {
  return new Promise((resolve) => {
    const BATCH_SIZE = 1000;
    let index = 0;

    const processBatch = () => {
      const end = Math.min(index + BATCH_SIZE, routesToFilter.length);
      index = end;

      if (index < routesToFilter.length) {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(processBatch, { timeout: 100 });
        } else {
          setTimeout(processBatch, 0);
        }
      } else {
        // Appliquer le filtrage sur toutes les routes
        const filtered = filterService.filterRoutes(routesToFilter, filtersToApply);
        resolve(filtered);
      }
    };

    processBatch();
  });
};

/**
 * Hook personnalisé pour filtrer les routes selon les critères sélectionnés
 */
export const useFilteredRoutes = (
  routes: Route[],
  filters: Filters
): { filteredRoutes: Route[]; isFiltering: boolean } => {
  const [filteredRoutes, setFilteredRoutes] = useState<Route[]>(routes);
  const [isFiltering, setIsFiltering] = useState(false);

  const deferredFilters = useDeferredValue(filters);

  useEffect(() => {
    if (routes.length === 0) {
      setFilteredRoutes([]);
      setIsFiltering(false);
      return;
    }

    setIsFiltering(true);

    const abortController = new AbortController();

    filterRoutesInBatches(routes, deferredFilters).then((result) => {
      if (!abortController.signal.aborted) {
        setFilteredRoutes(result);
        setIsFiltering(false);
      }
    });

    return () => {
      abortController.abort();
    };
  }, [routes, deferredFilters]);

  const isPending = filters !== deferredFilters;

  return {
    filteredRoutes,
    isFiltering: isFiltering || isPending
  };
};
